import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { doTrackedRedirect } from "../lib/track";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import ShareButton from "../components/ShareButton";
import {
  ShoppingBag, Sparkles, ArrowRight, CheckCircle2, ShieldCheck,
  Zap, Search, TrendingUp, Package,
} from "lucide-react";

const CATEGORY_LABELS = {
  fashion: { label: "Fashion", tagline: "Myntra · Ajio · Nykaa" },
  beauty: { label: "Beauty", tagline: "Nykaa · Mcaffeine · Wow" },
  health: { label: "Health", tagline: "HealthKart · Netmeds" },
  electronics: { label: "Electronics", tagline: "Tata Cliq · Croma · Lenovo" },
  travel: { label: "Travel", tagline: "Cleartrip · Flights & Hotels" },
  gifts: { label: "Gifts", tagline: "IGP · Curated for occasions" },
  jewelry: { label: "Jewelry", tagline: "Tiarra · Everyday luxe" },
  home: { label: "Home Care", tagline: "Koparo Clean" },
  subscription: { label: "Subscriptions", tagline: "Times Prime & more" },
  shopping: { label: "Everything Store", tagline: "Flipkart" },
  deals: { label: "Lightning Deals", tagline: "ZOP" },
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [mobileCapture, setMobileCapture] = useState(null); // {product} while modal open

  useEffect(() => {
    api.get("/products", { params: { type: "ecom" } }).then((r) => {
      const items = r.data || [];
      setTotalBrands(items.length);
      // Featured picks — well-known brand names first. Cleartrip is app-only
      // and its web tracker URL returns 200 but no visible redirect; keeping
      // it out of the priority list until we have a validated deep link.
      const priority = ["Myntra", "Flipkart", "Ajio", "Nykaa Beauty", "Nykaa Fashion", "Tata Cliq", "Croma", "Mcaffeine"];
      const featured = [
        ...priority.map((n) => items.find((p) => p.title === n)).filter(Boolean),
        ...items.filter((p) => !priority.includes(p.title)),
      ].slice(0, 8);
      setFeatured(featured);
    });
  }, []);

  // Click any product tile / card / featured brand → shortest possible journey
  // to the partner site. No intermediate detail page (per user feedback 2026-07-24).
  const openProduct = (product) => (e) => {
    e?.preventDefault?.();
    // Banking clicks ALWAYS ask for mobile — lenders need a fresh phone per
    // lead attempt for eligibility. Dialog pre-fills from cookie on repeat.
    if (product.type === "banking") {
      setMobileCapture(product);
      return;
    }
    doTrackedRedirect(product);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-14 items-center">
            <div>
              <Badge variant="secondary" className="badge-mono mb-5" data-testid="hero-badge">
                <Sparkles className="w-3 h-3 mr-1.5" /> Trusted by 5cr+ Indians
              </Badge>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
                Shop the brands you love.<br />
                <span className="text-[#0055FF] italic">Earn</span> on every ₹ spent.
              </h1>
              <p className="text-lg text-neutral-600 mt-6 max-w-xl">
                Compare offers across {totalBrands || "40+"} top brands — Myntra, Flipkart, Ajio, Nykaa, Cleartrip and more. Click, shop, and get cashback tracked automatically to your account.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/marketplace?type=ecom" data-testid="hero-shop-cta">
                  <Button size="lg" className="h-12 px-6 bg-black hover:bg-[#0055FF] text-white">
                    <ShoppingBag className="w-4 h-4 mr-2" /> Browse all brands
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/marketplace?type=ecom&category=fashion">
                  <Button size="lg" variant="outline" className="h-12 px-5" data-testid="hero-fashion-cta">
                    Fashion deals
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-neutral-600">
                <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00A36C]" /> Secure tracking</span>
                <span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-[#00A36C]" /> No hidden fees</span>
              </div>
            </div>

            {/* Right: rotating brand tile grid. Card body -> product detail
                (allows browsing). Hover overlay + explicit CTA below the card
                trigger direct partner URL open. */}
            <div className="grid grid-cols-2 gap-3">
              {featured.slice(0, 6).map((p) => (
                <div key={p.id} className="flex flex-col gap-2">
                  <Link
                    to={`/product/${p.id}`}
                    data-testid={`hero-brand-${p.id}`}
                    className="group relative aspect-square rounded-xl border border-black/10 bg-white overflow-hidden hover:border-black transition-colors text-left flex flex-col"
                  >
                    <div className="flex-1 flex items-center justify-center p-6 bg-white">
                      {p.image_url && (
                        <img
                          src={p.image_url}
                          alt={p.title}
                          className="max-w-full max-h-full object-contain group-hover:scale-[1.03] transition-transform duration-300"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="px-3 py-2 border-t border-black/5 bg-neutral-50">
                      <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500">{p.category}</div>
                      <div className="font-display text-sm font-semibold truncate text-black">{p.title}</div>
                    </div>
                    {/* Hover overlay CTA — small corner pill.  Absolute
                        positioning + pointer-events-none by default so the
                        parent Link handles card-body clicks; only the pill
                        itself catches its own click. */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openProduct(p)(e); }}
                      data-testid={`hero-brand-overlay-${p.id}`}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-[11px] font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1 shadow-md z-10"
                    >
                      Shop <ArrowRight className="w-3 h-3" />
                    </button>
                  </Link>
                  {/* Share button — outside the Link so its own onClick can
                      handle share flow without navigating away. */}
                  <ShareButton product={p} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOANS & BANKING — CTA-only banner. Individual lenders live on the
          Loan Express subdomain (loans.offer.express) per business decision
          2026-08-04. Main site never shows the loan card grid — always render
          this banner so users know where to find loans. */}
      <section className="bg-neutral-50 border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="badge-mono text-neutral-500 mb-2">Loans & Banking</div>
              <h2 className="font-display text-4xl font-bold tracking-tighter">Instant loans on the Loan Express</h2>
              <p className="text-neutral-600 mt-3 max-w-lg">
                Bank-grade partners · Cashback on disbursal · 100% digital · Instant approval.
              </p>
            </div>
            <a
              href={typeof window !== "undefined" && /localhost|preview\.emergentagent/.test(window.location.hostname)
                ? "/?loans=1"
                : "https://loans.offer.express/"}
              data-testid="see-all-loans"
              className="inline-flex items-center gap-2 bg-black text-white text-sm font-semibold rounded-full px-6 py-3 hover:bg-neutral-800 transition-colors"
            >
              See all loans <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-y border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Package, label: "Partner brands", value: `${totalBrands}+` },
            { icon: TrendingUp, label: "Networks integrated", value: "3" },
            { icon: CheckCircle2, label: "Click-to-cashback", value: "Real-time" },
            { icon: Search, label: "Categories", value: "12+" },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-neutral-100 grid place-items-center flex-shrink-0">
                <Icon className="w-5 h-5 text-neutral-700" />
              </div>
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="badge-mono text-neutral-500 mb-2">Shop by category</div>
            <h2 className="font-display text-4xl font-bold tracking-tighter">Pick what you&apos;re shopping for</h2>
          </div>
          <Link to="/marketplace?type=ecom" data-testid="see-all-brands" className="text-sm text-neutral-500 hover:text-black inline-flex items-center gap-1">
            See all brands <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(CATEGORY_LABELS).map(([slug, meta]) => (
            <Link
              key={slug}
              to={`/marketplace?type=ecom&category=${slug}`}
              data-testid={`cat-${slug}`}
              className="group p-5 rounded-xl border border-black/10 bg-white hover:border-black hover:shadow-sm transition-all"
            >
              <div className="font-display text-lg font-semibold tracking-tight group-hover:text-[#0055FF] transition-colors">{meta.label}</div>
              <div className="text-xs text-neutral-500 mt-1">{meta.tagline}</div>
              <ArrowRight className="w-4 h-4 mt-4 text-neutral-400 group-hover:text-[#0055FF] transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-neutral-50 border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="badge-mono text-neutral-500 mb-2">How it works</div>
          <h2 className="font-display text-4xl font-bold tracking-tighter mb-10">Three clicks. One reward.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Pick a brand", body: "Browse 40+ partners across fashion, beauty, health, travel and more." },
              { step: "02", title: "Shop as usual", body: "We redirect you to the brand's website — no coupon codes, no extra steps." },
              { step: "03", title: "Cashback tracked", body: "The network attributes the sale to your account. Cashback lands in your wallet." },
            ].map((s) => (
              <div key={s.step} className="p-6 rounded-xl bg-white border border-black/10">
                <div className="badge-mono text-neutral-400 mb-4">{s.step}</div>
                <div className="font-display text-xl font-semibold tracking-tight mb-2">{s.title}</div>
                <p className="text-sm text-neutral-600">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BRANDS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="badge-mono text-neutral-500 mb-2">Handpicked for you</div>
            <h2 className="font-display text-4xl font-bold tracking-tighter">Trending brands this week</h2>
          </div>
          <Link to="/marketplace?type=ecom" className="text-sm text-neutral-500 hover:text-black inline-flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {featured.map((p) => (
            <div key={p.id} className="flex flex-col">
              <Link
                to={`/product/${p.id}`}
                data-testid={`featured-${p.id}`}
                className="group aspect-[4/5] rounded-xl border border-black/10 bg-white overflow-hidden hover:border-black hover:shadow-md transition-all relative text-left flex flex-col"
              >
                <div className="absolute top-3 left-3 z-10">
                  <Badge variant="secondary" className="badge-mono bg-neutral-100 text-neutral-700">{p.category}</Badge>
                </div>
                <div className="flex-1 flex items-center justify-center p-8 bg-white">
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="max-w-[70%] max-h-[70%] object-contain group-hover:scale-[1.05] transition-transform duration-300"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="px-4 py-3 border-t border-black/5 bg-neutral-50">
                  <div className="font-display text-base font-semibold tracking-tight truncate">{p.title}</div>
                  <div className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1">
                    View details <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
                {/* Hover pill — small, only itself is clickable */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openProduct(p)(e); }}
                  data-testid={`featured-overlay-${p.id}`}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-xs font-semibold rounded-full px-3.5 py-2 inline-flex items-center gap-1 shadow-md z-10"
                >
                  Shop Now <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </Link>
              {/* Distinct row CTA below the card — always visible on mobile */}
              <button
                type="button"
                onClick={openProduct(p)}
                data-testid={`featured-cta-${p.id}`}
                className="mt-2 h-10 rounded-lg bg-black text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-neutral-800 transition-colors"
              >
                Shop Now <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <div className="mt-2">
                <ShareButton product={p} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="rounded-2xl bg-black text-white p-10 md:p-14 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <Badge className="bg-[#0055FF] hover:bg-[#0055FF] text-white mb-4">Ready when you are</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-4">
              Start earning on your everyday shopping.
            </h2>
            <p className="text-neutral-300 mb-8">
              No paperwork. No subscriptions. Just click a brand, shop, and let us handle the tracking.
            </p>
            <Link to="/marketplace?type=ecom" data-testid="cta-explore">
              <Button size="lg" className="h-12 px-6 bg-white text-black hover:bg-neutral-200">
                Explore all {totalBrands} brands <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="absolute -right-16 -bottom-16 w-96 h-96 rounded-full bg-[#0055FF]/20 blur-3xl" />
        </div>
      </section>

      {/* Mobile capture modal for banking cards on Home */}
      <MobileCaptureDialog
        open={!!mobileCapture}
        onOpenChange={(v) => { if (!v) setMobileCapture(null); }}
        product={mobileCapture}
        onCaptured={async () => {
          if (mobileCapture) await doTrackedRedirect(mobileCapture);
          setMobileCapture(null);
        }}
      />
    </div>
  );
}
